<template>
  <div class="fence-page">
    <!-- 左侧围栏列表 -->
    <div class="fence-list-panel">
      <div class="panel-header">
        <div class="flex justify-end">
            <a-button type="primary" size="small" @click="openAddModal">
            <template #icon><PlusOutlined /></template>
            添加围栏
            </a-button>
        </div>
        
      </div>

      <a-table
        :columns="columns"
        :row-key="(record) => record.id"
        :data-source="dataSource"
        size="middle"
        :pagination="pagination"
        :scroll="{ y: 'auto' }"
        v-autoHeight="{ extraHeight: 120, trHeight: 47 }"
        :row-class-name="(record) => record.id === selectedFenceId ? 'ant-table-row-selected' : ''"
        :customRow="customRow"
      >
        <template #bodyCell="{ column, record, index }">
          <template v-if="column.key === 'ranking'">
            {{ (pagination.current - 1) * pagination.pageSize + index + 1 }}
          </template>
          <template v-if="column.key === 'direction'">
            {{ getDirectionText(record.direction) }}
          </template>
          <template v-if="column.key === 'bufferDistance'">
            {{ record.bufferDistance != null ? record.bufferDistance + ' m' : '-' }}
          </template>
          <template v-if="column.key === 'speed'">
            {{ record.minSpeed != null || record.maxSpeed != null ? `${record.minSpeed ?? '-'} - ${record.maxSpeed ?? '-'} km/h` : '-' }}
          </template>
          <template v-else-if="column.key === 'action'">
            <span>
              <a class="ml10" @click="handleEdit(record)">修改</a>
              <a class="ml10" @click="handleRegulate(record)">管制规则</a>
              <a class="ml10" style="color: #ff4d4f" @click="handleDelete(record)">删除</a>
            </span>
          </template>
        </template>
      </a-table>
    </div>

    <!-- 右侧地图 -->
    <div class="map-panel">
      <div id="fence-map-container" class="map-container"></div>
      <div v-if="mapLoading" class="loading-overlay">
        <a-spin size="large" />
        <span class="loading-text">地图加载中...</span>
      </div>
      <!-- 围栏详情信息浮层 -->
      <div v-if="selectedFence" class="fence-info-overlay">
        <div class="fence-info-header">
          <span class="fence-info-title">{{ selectedFence.fenceName }}</span>
          <a class="fence-info-close" @click="clearSelection">×</a>
        </div>
        <div class="fence-info-content">
          <div class="fence-info-section">
            <div class="section-label">基本信息</div>
            <div class="fence-info-row">
              <span class="fence-info-label">城市</span>
              <span class="fence-info-value">{{ selectedFence.cityName || '-' }}</span>
            </div>
            <div class="fence-info-row">
              <span class="fence-info-label">区域</span>
              <span class="fence-info-value">{{ selectedFence.areaName || '-' }}</span>
            </div>
            <div class="fence-info-row">
              <span class="fence-info-label">方向</span>
              <span class="fence-info-value">{{ getDirectionText(selectedFence.direction) }}</span>
            </div>
            <div class="fence-info-row">
              <span class="fence-info-label">缓冲距离</span>
              <span class="fence-info-value">{{ selectedFence.bufferDistance != null ? selectedFence.bufferDistance + ' m' : '-' }}</span>
            </div>
            <div class="fence-info-row">
              <span class="fence-info-label">最小限速</span>
              <span class="fence-info-value">{{ selectedFence.minSpeed != null ? selectedFence.minSpeed + ' km/h' : '-' }}</span>
            </div>
            <div class="fence-info-row">
              <span class="fence-info-label">最大限速</span>
              <span class="fence-info-value">{{ selectedFence.maxSpeed != null ? selectedFence.maxSpeed + ' km/h' : '-' }}</span>
            </div>
          </div>
          <div v-if="selectedFence.policyList && selectedFence.policyList.length > 0" class="fence-info-section">
            <div class="section-label">策略列表</div>
            <div v-for="(policy, idx) in selectedFence.policyList" :key="idx" class="policy-card">
              <div class="fence-info-row">
                <span class="fence-info-label">规则名称</span>
                <span class="fence-info-value">{{ policy.ruleName || '-' }}</span>
              </div>
              <div class="fence-info-row">
                <span class="fence-info-label">规则类型</span>
                <span class="fence-info-value">{{ getRuleTypeText(policy.ruleType) }}</span>
              </div>
              <div class="fence-info-row">
                <span class="fence-info-label">适用日期</span>
                <span class="fence-info-value">{{ getRuleWeekDaysText(policy) }}</span>
              </div>
              <div class="fence-info-row">
                <span class="fence-info-label">禁行时段</span>
                <span class="fence-info-value">{{ getRuleTimeSlotsText(policy.timeSlots) }}</span>
              </div>
              <div class="fence-info-row">
                <span class="fence-info-label">优先级</span>
                <span class="fence-info-value">{{ policy.priority ?? '-' }}</span>
              </div>
              <div class="fence-info-row">
                <span class="fence-info-label">生效日期</span>
                <span class="fence-info-value">{{ policy.effectiveStartDate || '-' }} ~ {{ policy.effectiveEndDate || '-' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加/修改围栏弹框 -->
    <a-modal
      v-model:open="modalVisible"
      :title="editingFence.id ? '修改围栏' : '添加围栏'"
      :width="640"
      @ok="handleSubmit"
      :confirm-loading="submitLoading"
    >
      <a-form :model="editingFence" layout="vertical">
        <a-row :gutter="24">
          <a-col :span="12">
            <a-form-item label="围栏名称" required>
              <a-input v-model:value="editingFence.fenceName" placeholder="请输入围栏名称" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="方向" required>
              <a-select v-model:value="editingFence.direction" placeholder="请选择方向">
                <a-select-option :value="0">双向</a-select-option>
                <a-select-option :value="1">正向</a-select-option>
                <a-select-option :value="2">反向</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>
        <a-form-item label="几何数据(geometry)" required>
          <a-textarea
            v-model:value="editingFence.geometry"
            placeholder='请输入 GeoJSON 几何数据，如：{"type":"Polygon","coordinates":[[[lng,lat],...]]}'
            :rows="4"
          />
          <span class="form-hint">支持 Polygon / LineString / Point 类型</span>
        </a-form-item>
        <a-row :gutter="24">
          <a-col :span="8">
            <a-form-item label="缓冲距离(m)">
              <a-input-number v-model:value="editingFence.bufferDistance" placeholder="请输入" style="width: 100%" :min="0" />
            </a-form-item>
          </a-col>
          <a-col :span="8">
            <a-form-item label="最小限速(km/h)">
              <a-input-number v-model:value="editingFence.minSpeed" placeholder="请输入" style="width: 100%" :min="0" />
            </a-form-item>
          </a-col>
          <a-col :span="8">
            <a-form-item label="最大限速(km/h)">
              <a-input-number v-model:value="editingFence.maxSpeed" placeholder="请输入" style="width: 100%" :min="0" />
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-modal>

    <!-- 围栏规则弹框 -->
    <a-modal
      v-model:open="ruleModalVisible"
      :title="`规则列表${currentFence?.fenceName ? ` - ${currentFence.fenceName}` : ''}`"
      :width="1000"
      :footer="null"
      @cancel="resetRuleModal"
    >
      <div class="rule-modal-toolbar">
        <a-button type="primary" size="small" @click="handleAddRuleRow" :disabled="editingRuleRowKey !== null">
          <template #icon><PlusOutlined /></template>
          新增
        </a-button>
      </div>
      <a-table
        :columns="ruleColumns"
        :data-source="ruleDataSource"
        :row-key="(record) => record.id"
        :pagination="false"
        size="small"
        :scroll="{ x: 960 }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'ruleName'">
            <a-select
              v-if="record.isNew && editingRuleRowKey === record.id"
              v-model:value="record.ruleId"
              show-search
              placeholder="请选择规则"
              option-filter-prop="label"
              :options="ruleSelectOptions"
              style="width: 100%"
              @change="(value) => handleRuleSelect(record, value)"
            />
            <span v-else>{{ record.ruleName ?? '-' }}</span>
          </template>
          <template v-if="column.key === 'ruleType'">
            {{ getRuleTypeText(record.ruleType) }}
          </template>
          <template v-if="column.key === 'weekDays'">
            {{ record.isNew && editingRuleRowKey === record.id ? record.weekDaysText : getRuleWeekDaysText(record) }}
          </template>
          <template v-if="column.key === 'timeSlots'">
            {{ record.isNew && editingRuleRowKey === record.id ? record.timeSlotsText : getRuleTimeSlotsText(record.timeSlots) }}
          </template>
          <template v-if="column.key === 'priority'">
            <a-input-number
              v-if="editingRuleRowKey === record.id"
              v-model:value="record.priority"
              :min="0"
              placeholder="请输入"
              style="width: 100%"
            />
            <span v-else>{{ record.priority ?? '-' }}</span>
          </template>
          <template v-if="column.key === 'effectiveDate'">
            <a-range-picker
              v-if="editingRuleRowKey === record.id"
              v-model:value="record.effectiveDateRange"
              format="YYYY-MM-DD"
              style="width: 100%"
            />
            <span v-else>{{ getEffectiveDateText(record) }}</span>
          </template>
          <template v-if="column.key === 'action'">
            <template v-if="editingRuleRowKey === record.id">
              <a @click="saveRuleRow(record)">保存</a>
              <a class="ml10" @click="cancelRuleEdit(record)">取消</a>
            </template>
            <template v-else>
              <a @click="handleRuleEdit(record)">修改</a>
              <a class="ml10" style="color: #ff4d4f" @click="handleRuleDelete(record)">删除</a>
            </template>
          </template>
        </template>
      </a-table>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { message, Modal } from 'ant-design-vue';
import { PlusOutlined } from '@ant-design/icons-vue';
import AMapLoader from '@amap/amap-jsapi-loader';
import {
  getFenceList,
  getFenceDetail,
  saveRoadsFence,
  updateRoadsFence,
  deleteRoadsFence,
} from '@/api/roads/fence.js';
import { getFenceRuleList, saveFenceRule, deleteFenceRule } from '@/api/roads/fenceRule.js';
import { getPageList } from '@/api/roads/restrictionTimeRule.js';
import dayjs from 'dayjs';

const router = useRouter();
const route = useRoute();

// 列表相关
const dataSource = ref([]);
const selectedFenceId = ref(null);
const selectedFence = ref(null);
const mapLoading = ref(false);
let map = null;
let AMapInstance = null;
let allOverlays = [];
let highlightOverlay = null;

const columns = [
  { title: '序号', key: 'ranking', width: 50 },
  { title: '围栏名称', dataIndex: 'fenceName', key: 'fenceName', width: 200 },
  { title: '操作', key: 'action', width: 160 },
];

const pagination = reactive({
  total: 0,
  size: 'middle',
  defaultPageSize: 10,
  current: 1,
  pageSize: 10,
  showSizeChanger: true,
  showTotal: (total) => `共有 ${total} 条数据`,
  onShowSizeChange: (current, pageSize) => (pagination.pageSize = pageSize),
  onChange: pageChange,
  showSizeChange: pageSizeChange,
});

function pageChange(page, pageSize) {
  pagination.current = page;
  getData();
}

function pageSizeChange(current, size) {
  pagination.pageSize = size;
  getData();
}

const customRow = (record) => ({
  onClick: () => {
    handleSelectFence(record);
  },
  style: { cursor: 'pointer' },
});

const getDirectionText = (direction) => {
  const map = { 0: '双向', 1: '正向', 2: '反向' };
  return map[direction] ?? '-';
};

// 获取列表
const getData = async () => {

  try {
    const res = await getFenceList();
    if (res.code === 200) {
      dataSource.value = res.data || [];
      pagination.total = dataSource.value.length;
      drawAllFences();
    }
  } catch (error) {
    console.error('获取围栏列表失败:', error);
    message.error('获取围栏列表失败');
  }
};

// 初始化地图
const initMap = async () => {
  mapLoading.value = true;
  try {
    const mapContainer = document.getElementById('fence-map-container');
    if (mapContainer.offsetWidth === 0 || mapContainer.offsetHeight === 0) {
      mapContainer.style.minHeight = '400px';
      await nextTick();
    }

    const AMap = await AMapLoader.load({
      key: '6588d9e5b6ae0751c799cb4c7b3e30be',
      version: '2.0',
      plugins: ['AMap.ToolBar', 'AMap.Scale'],
    }).catch((error) => {
      throw new Error(`高德地图API加载失败: ${error.message || error}`);
    });

    AMapInstance = AMap;
    map = new AMap.Map('fence-map-container', {
      zoom: 12,
      center: [103.8349, 36.0680],
      mapStyle: 'amap://styles/whitesmoke',
      viewMode: '2D',
      zooms: [2, 20],
      dragEnable: true,
      animateEnable: true,
      showZoomControl: false,
    });

    map.addControl(new AMap.ToolBar({ position: 'RB', offset: [10, 60], liteStyle: true }));

    const handleResize = () => {
      if (map) {
        try {
          map.resize();
        } catch (error) {
          console.warn('地图尺寸调整失败:', error);
        }
      }
    };
    window.addEventListener('resize', handleResize);

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });
  } catch (error) {
    console.error('地图初始化失败:', error);
    message.error(error.message || '地图初始化失败，请刷新页面重试');
  } finally {
    mapLoading.value = false;
  }
};

// 清理所有地图覆盖物
const clearAllOverlays = () => {
  if (!map) return;
  allOverlays.forEach((overlay) => {
    try {
      map.remove(overlay);
    } catch (e) {
      /* ignore */
    }
  });
  allOverlays = [];
  if (highlightOverlay) {
    try { map.remove(highlightOverlay); } catch (e) { /* ignore */ }
    highlightOverlay = null;
  }
};

// 解析几何数据并返回 bounds 坐标
const parseGeometry = (fence) => {
  if (!fence.geometry) return null;
  try {
    const geometry = typeof fence.geometry === 'string' ? JSON.parse(fence.geometry) : fence.geometry;
    return geometry;
  } catch (error) {
    console.error('解析围栏几何数据失败:', error);
    return null;
  }
};

// 绘制单个围栏覆盖物
const createFenceOverlay = (geometry, isSelected = false) => {
  const { type, coordinates } = geometry;
  const fillColor = isSelected ? '#cf1322' : '#ff4d4f';
  const strokeColor = isSelected ? '#cf1322' : '#ff4d4f';
  const fillOpacity = isSelected ? 0.35 : 0.15;
  const strokeWeight = isSelected ? 3 : 2;

  if (type === 'Polygon') {
    const ring = coordinates[0] || [];
    const path = ring.map(([lng, lat]) => new AMapInstance.LngLat(lng, lat));
    return new AMapInstance.Polygon({
      path,
      fillColor,
      fillOpacity,
      strokeColor,
      strokeWeight,
    });
  } else if (type === 'LineString') {
    const path = coordinates.map(([lng, lat]) => new AMapInstance.LngLat(lng, lat));
    return new AMapInstance.Polyline({
      path,
      strokeColor,
      strokeWeight,
    });
  } else if (type === 'Point') {
    const [lng, lat] = coordinates;
    return new AMapInstance.Marker({
      position: [lng, lat],
    });
  }
  return null;
};

// 从几何数据中提取 bounds 坐标
const getBoundsFromGeometry = (geometry) => {
  const { type, coordinates } = geometry;
  if (type === 'Polygon') {
    return coordinates[0] || [];
  } else if (type === 'LineString') {
    return coordinates;
  } else if (type === 'Point') {
    return [coordinates];
  }
  return [];
};

// 绘制所有围栏
const drawAllFences = () => {
  if (!map || !AMapInstance) return;
  clearAllOverlays();

  const allBounds = [];

  dataSource.value.forEach((fence) => {
    const geometry = parseGeometry(fence);
    if (!geometry) return;

    const overlay = createFenceOverlay(geometry, false);
    if (overlay) {
      map.add(overlay);
      allOverlays.push(overlay);
    }

    const bounds = getBoundsFromGeometry(geometry);
    allBounds.push(...bounds);
  });

  // 适配所有围栏的视野
  if (allBounds.length > 0) {
    const lngs = allBounds.map((c) => c[0]);
    const lats = allBounds.map((c) => c[1]);
    const southWest = [Math.min(...lngs), Math.min(...lats)];
    const northEast = [Math.max(...lngs), Math.max(...lats)];
    map.setBounds(new AMapInstance.Bounds(southWest, northEast), false, [50, 50, 50, 50]);
  }
};

// 聚焦选中围栏
const focusFence = (fence) => {
  if (!map || !AMapInstance) return;

  const geometry = parseGeometry(fence);
  if (!geometry) return;

  // 聚焦到选中围栏
  const bounds = getBoundsFromGeometry(geometry);
  if (bounds.length > 0) {
    const lngs = bounds.map((c) => c[0]);
    const lats = bounds.map((c) => c[1]);
    const southWest = [Math.min(...lngs), Math.min(...lats)];
    const northEast = [Math.max(...lngs), Math.max(...lats)];
    map.setBounds(new AMapInstance.Bounds(southWest, northEast), false, [80, 80, 80, 80]);
  }
};

// 选中围栏查看
const handleSelectFence = async (record) => {
  selectedFenceId.value = record.id;
  focusFence(record);
  try {
    const res = await getFenceDetail(record.id);
    if (res.code === 200) {
      selectedFence.value = res.data || {};
    }
  } catch (error) {
    console.error('获取围栏详情失败:', error);
    message.error('获取围栏详情失败');
  }
};

// 取消选中
const clearSelection = () => {
  selectedFenceId.value = null;
  selectedFence.value = null;
  drawAllFences();
};

// 添加/修改弹框
const modalVisible = ref(false);
const submitLoading = ref(false);
const editingFence = reactive({
  id: null,
  fenceName: '',
  geometry: '',
  bufferDistance: null,
  minSpeed: null,
  maxSpeed: null,
  direction: 0,
});

const resetEditingFence = () => {
  editingFence.id = null;
  editingFence.fenceName = '';
  editingFence.geometry = '';
  editingFence.bufferDistance = null;
  editingFence.minSpeed = null;
  editingFence.maxSpeed = null;
  editingFence.direction = 0;
};

const openAddModal = () => {
  resetEditingFence();
  modalVisible.value = true;
};

const handleEdit = (record) => {
  Object.assign(editingFence, record);
  editingFence.id = record.id;
  modalVisible.value = true;
};

const handleSubmit = async () => {
  if (!editingFence.fenceName) {
    message.error('请填写围栏名称');
    return;
  }
  if (!editingFence.geometry) {
    message.error('请填写几何数据');
    return;
  }
  if (editingFence.direction === undefined || editingFence.direction === null) {
    message.error('请选择方向');
    return;
  }

  submitLoading.value = true;
  try {
    const payload = { ...editingFence };
    const res = editingFence.id
      ? await updateRoadsFence(payload)
      : await saveRoadsFence(payload);
    if (res.code === 200) {
      message.success(editingFence.id ? '修改成功' : '添加成功');
      modalVisible.value = false;
      getData();
    }
  } catch (error) {
    console.error('保存围栏失败:', error);
    message.error('保存围栏失败');
  } finally {
    submitLoading.value = false;
  }
};

const handleDelete = (record) => {
  Modal.confirm({
    title: '确认删除',
    content: `确定要删除围栏「${record.fenceName}」吗？`,
    okText: '确认',
    cancelText: '取消',
    okType: 'danger',
    onOk: async () => {
      try {
        const res = await deleteRoadsFence({ id: record.id });
        if (res.code === 200) {
          message.success('删除成功');
          if (selectedFenceId.value === record.id) {
            selectedFenceId.value = null;
            selectedFence.value = null;
            clearAllOverlays();
          }
          getData();
        }
      } catch (error) {
        console.error('删除围栏失败:', error);
        message.error('删除围栏失败');
      }
    },
  });
};

const goBack = () => {
  router.back();
};

// ========== 围栏规则弹框 ==========
const ruleModalVisible = ref(false);
const currentFence = ref(null);
const ruleDataSource = ref([]);
const ruleOptions = ref([]);
const editingRuleRowKey = ref(null);

const ruleColumns = [
  { title: '规则名称', key: 'ruleName', width: 160 },
  { title: '规则类型', key: 'ruleType', width: 100 },
  { title: '适用日期', key: 'weekDays', width: 190 },
  { title: '禁行时段', key: 'timeSlots', width: 150 },
  { title: '优先级',   key: 'priority', width: 100 },
  { title: '规则生效日期', key: 'effectiveDate', width: 220 },
  { title: '操作', key: 'action', width: 100, fixed: 'right' },
];

const ruleSelectOptions = computed(() =>
  ruleOptions.value.map((r) => ({ label: r.ruleName, value: r.id }))
);

// 打开规则弹框
const handleRegulate = (record) => {
  currentFence.value = record;
  ruleModalVisible.value = true;
  fetchRuleOptions();
  getFenceRuleData();
};

const resetRuleModal = () => {
  editingRuleRowKey.value = null;
  currentFence.value = null;
  ruleDataSource.value = [];
};

// 获取围栏规则列表
const getFenceRuleData = async () => {
  try {
    const res = await getFenceRuleList(currentFence.value.id);
    if (res.code === 200) {
      ruleDataSource.value = (res.data || []).map((item) => ({
        ...item,
        isNew: false,
        effectiveDateRange:
          item.effectiveStartDate && item.effectiveEndDate
            ? [dayjs(item.effectiveStartDate, 'YYYY-MM-DD'), dayjs(item.effectiveEndDate, 'YYYY-MM-DD')]
            : null,
      }));
    }
  } catch (error) {
    console.error('获取围栏规则列表失败:', error);
    message.error('获取围栏规则列表失败');
  }
};

// 获取规则下拉数据
const fetchRuleOptions = async () => {
  try {
    const res = await getPageList({ pageIndex: 1, pageSize: 100, conditionMap: {} });
    if (res.code === 200) {
      ruleOptions.value = res.data.records || [];
    }
  } catch (error) {
    console.error('获取规则下拉数据失败:', error);
  }
};

// 新增规则行
const handleAddRuleRow = () => {
  const newId = `new_${Date.now()}`;
  ruleDataSource.value.push({
    id: newId,
    isNew: true,
    ruleId: undefined,
    ruleName: '',
    ruleType: null,
    weekDayText: '-',
    timeSlotsText: '-',
    ruleWeight: undefined,
    effectiveDateRange: null,
  });
  editingRuleRowKey.value = newId;
};

// 选中规则后回显其他项
const handleRuleSelect = (record, ruleId) => {
  const rule = ruleOptions.value.find((r) => r.id === ruleId);
  if (rule) {
    record.ruleName = rule.ruleName;
    record.ruleType = rule.ruleType;
    record.weekDaysText = getRuleWeekDaysText(rule);
    record.timeSlotsText = getRuleTimeSlotsText(rule.timeSlots);
  }
};

// 编辑规则行（可修改权重、生效日期）
const handleRuleEdit = (record) => {
  editingRuleRowKey.value = record.id;
};

// 保存规则行
const saveRuleRow = async (record) => {
  if (record.isNew && !record.ruleId) {
    message.error('请选择规则');
    return;
  }
  if (record.priority == null) {
    message.error('请填写优先级');
    return;
  }
  if (!record.effectiveDateRange || record.effectiveDateRange.length !== 2) {
    message.error('请选择规则生效日期');
    return;
  }

  try {
    const payload = {
      id: record.isNew ? null : record.id,
      fenceId: currentFence.value.id,
      ruleId: record.ruleId,
      priority: record.priority,
      effectiveStartDate: record.effectiveDateRange[0].format('YYYY-MM-DD'),
      effectiveEndDate: record.effectiveDateRange[1].format('YYYY-MM-DD'),
    };
    const res = await saveFenceRule(payload);
    if (res.code === 200) {
      message.success('保存成功');
      editingRuleRowKey.value = null;
      getFenceRuleData();
    }
  } catch (error) {
    console.error('保存围栏规则失败:', error);
    message.error('保存围栏规则失败');
  }
};

// 取消编辑
const cancelRuleEdit = (record) => {
  editingRuleRowKey.value = null;
  if (record.isNew) {
    ruleDataSource.value = ruleDataSource.value.filter((r) => r.id !== record.id);
  } else {
    getFenceRuleData();
  }
};

// 删除规则行
const handleRuleDelete = (record) => {
  Modal.confirm({
    title: '确认删除',
    content: `确定要删除规则「${record.ruleName}」吗？`,
    okText: '确认',
    cancelText: '取消',
    okType: 'danger',
    onOk: async () => {
      try {
        const res = await deleteFenceRule({ id: record.id });
        if (res.code === 200) {
          message.success('删除成功');
          getFenceRuleData();
        }
      } catch (error) {
        console.error('删除围栏规则失败:', error);
        message.error('删除围栏规则失败');
      }
    },
  });
};

// ========== 规则显示辅助 ==========
const getRuleTypeText = (type) => {
  return { 1: '按星期循环', 2: '按具体日期' }[type] || '-';
};

const weekDayText = (d) => ({
  1: '周一', 2: '周二', 3: '周三', 4: '周四',
  5: '周五', 6: '周六', 7: '周日',
}[d]);

const getRuleWeekDaysText = (rule) => {
  if (rule.ruleType === 1) {
    if (!rule.weekDays) return '-';
    return rule.weekDays.split(',').map(Number).map(weekDayText).join('、');
  }
  if (rule.ruleType === 2) {
    if (!rule.startDate || !rule.endDate) return '-';
    return `${rule.startDate} ~ ${rule.endDate}`;
  }
  return '-';
};

const getRuleTimeSlotsText = (timeSlots) => {
  let slots = timeSlots;
  if (typeof slots === 'string') {
    try { slots = JSON.parse(slots); } catch (e) { return '-'; }
  }
  if (!Array.isArray(slots) || slots.length === 0) return '-';
  return slots.map((s) => `${s.start}-${s.end}`).join('，');
};

const getEffectiveDateText = (record) => {
  if (!record.effectiveStartDate || !record.effectiveEndDate) return '-';
  return `${record.effectiveStartDate} ~ ${record.effectiveEndDate}`;
};

onMounted(async () => {
  await nextTick();
  await initMap();
  getData();
});

onBeforeUnmount(() => {
  if (map) {
    map.destroy();
    map = null;
  }
});
</script>

<style lang="less" scoped>
.fence-page {
  display: flex;
  gap: 16px;
  padding: 16px;
  height: calc(100vh - 70px);
  background-color: #ffffff;
}

.fence-list-panel {
  width: 450px;
  flex-shrink: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.map-panel {
  flex: 1;
  position: relative;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  min-width: 0;

  .map-container {
    width: 100%;
    height: 100%;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    z-index: 10;

    .loading-text {
      font-size: 14px;
      color: #666;
    }
  }

  .fence-info-overlay {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 380px;
    max-height: calc(100% - 24px);
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    z-index: 10;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .fence-info-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      background: linear-gradient(135deg, #1890ff, #096dd9);

      .fence-info-title {
        font-size: 15px;
        font-weight: 600;
        color: #fff;
      }

      .fence-info-close {
        font-size: 16px;
        color: rgba(255, 255, 255, 0.8);
        cursor: pointer;
        transition: color 0.2s;

        &:hover {
          color: #fff;
        }
      }
    }

    .fence-info-content {
      flex: 1;
      overflow-y: auto;
      padding: 4px 0;
    }

    .fence-info-section {
      padding: 8px 16px;

      .section-label {
        font-size: 12px;
        font-weight: 600;
        color: #8c8c8c;
        margin-bottom: 6px;
        padding-left: 6px;
        border-left: 3px solid #1890ff;
        line-height: 1;
      }
    }

    .fence-info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 5px 0;
      line-height: 1.5;

      .fence-info-label {
        font-size: 13px;
        color: #8c8c8c;
        flex-shrink: 0;
        white-space: nowrap;
      }

      .fence-info-value {
        font-size: 13px;
        color: #262626;
        text-align: right;
        margin-left: 12px;
        word-break: break-all;
      }
    }

    .policy-card {
      background: #f5f7fa;
      border-radius: 6px;
      padding: 10px 12px;
      margin: 6px 0;
      border-left: 3px solid #52c41a;
    }
  }
}

.form-hint {
  display: block;
  font-size: 12px;
  color: #999999;
  margin-top: 4px;
}

.rule-modal-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

/deep/ .ant-table-row-selected td {
  background-color: #e6f7ff !important;
}

/deep/ .ant-table-wrapper .ant-table-thead > tr > th,
.ant-table-wrapper .ant-table-thead > tr > td {
  font-size: 14px;
  font-family: PingFang SC-Regular, PingFang SC;
  font-weight: 400;
  color: rgba(10, 33, 46, 1);
  background-color: rgba(239, 240, 245, 1);
}

/deep/ .ant-table-wrapper .ant-table-thead > tr > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before,
.ant-table-wrapper .ant-table-thead > tr > td:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before {
  width: 0px;
}

a {
  color: #1E8EF2;
}
</style>
